import { useEffect } from 'react';

type JailRecord = {
  activeElement: Element | null;
  ref: { current: HTMLElement | null };
};

// Module-level stack so nested modals defer to the innermost jail.
// Mirrors GEL's `roots` array (global-event-listener/context.jsx:32).
const jails: JailRecord[] = [];

const isTopmostJail = (ref: { current: HTMLElement | null }): boolean => jails.length > 0 && jails[jails.length - 1].ref === ref;

// Mirrors GEL's iframe-aware top-window focus check (context.jsx:35-51).
const isWindowFocused = (): boolean => {
  try {
    if (global.window === global.window.top) {
      return true;
    }
    const activeEl = global.window.top?.document.activeElement;
    if (!activeEl || activeEl.nodeName !== 'IFRAME') {
      return false;
    }
    const frame = activeEl as HTMLIFrameElement;
    return frame.contentWindow === global.window;
  } catch {
    // Cross-origin iframe security error — we are not the focused iframe.
    return false;
  }
};

/**
 * Ports the GEL focus manager into a standalone hook.
 * When `active` is true (default), focus is kept inside `jailRef` via
 * focusin/focusout reclaim, a module-level jail stack, and focus-restore
 * on unmount. When `active` is false the hook is fully inert.
 *
 * Use the `active` flag to gate it behind the new-hotkey-system flag so the
 * OFF path continues to rely on GEL's own focus manager.
 */
export const useFocusJail = (jailRef: { current: HTMLElement | null }, { active = true }: { active?: boolean } = {}): void => {
  // focusin/focusout reclaim listeners.
  // Mirrors context.jsx:85-175.
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let isMounted = true;

    const focusInHandler = (e: Event) => {
      const target = e.target;

      // Allow Pendo (surveys, guides) to receive focus freely.
      const pendoContainer = global.document.getElementById('pendo-base');
      if (pendoContainer instanceof HTMLElement && target instanceof Node && pendoContainer.contains(target)) {
        return;
      }

      if (
        isTopmostJail(jailRef) &&
        isWindowFocused() &&
        target instanceof HTMLElement &&
        !target.closest('[data-ignore-root-focus]') &&
        jailRef.current !== null &&
        jailRef.current !== target &&
        !jailRef.current.contains(target)
      ) {
        jailRef.current.focus();
      }
    };

    const focusOutHandler = () => {
      setTimeout(() => {
        const activeEl = global.document.activeElement;

        // Allow Pendo to retain focus.
        const pendoContainer = global.document.getElementById('pendo-base');
        if (pendoContainer instanceof HTMLElement && activeEl instanceof Node && pendoContainer.contains(activeEl)) {
          return;
        }

        if (
          isMounted &&
          isTopmostJail(jailRef) &&
          isWindowFocused() &&
          jailRef.current !== null &&
          jailRef.current !== activeEl &&
          activeEl !== null &&
          activeEl.nodeName.toLowerCase() !== 'iframe' &&
          !(activeEl instanceof HTMLElement && activeEl.closest('[data-ignore-root-focus]')) &&
          !jailRef.current.contains(activeEl)
        ) {
          jailRef.current.focus();
        }
      }, 0);
    };

    global.document.addEventListener('focusin', focusInHandler);
    global.document.addEventListener('focusout', focusOutHandler);

    return () => {
      isMounted = false;
      global.document.removeEventListener('focusin', focusInHandler);
      global.document.removeEventListener('focusout', focusOutHandler);
    };
  }, [active, jailRef]);

  // Push onto the jails stack on mount; pop and restore focus on unmount.
  // Mirrors context.jsx:177-204.
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const record: JailRecord = {
      activeElement: global.document.activeElement,
      ref: jailRef,
    };

    jails.push(record);

    if (jailRef.current !== null && !jailRef.current.contains(global.document.activeElement)) {
      jailRef.current.focus();
    }

    return () => {
      const idx = jails.indexOf(record);
      if (idx >= 0) {
        jails.splice(idx, 1);
      }

      const prev = record.activeElement;
      if (prev instanceof HTMLElement && prev.isConnected) {
        prev.focus();
      } else {
        const parentRecord = jails[jails.length - 1];
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- ref access in cleanup is intentional
        if (parentRecord?.ref.current !== null && parentRecord?.ref.current !== undefined) {
          // oxlint-disable-next-line react-hooks/exhaustive-deps -- ref access in cleanup is intentional
          parentRecord.ref.current.focus();
        }
      }
    };
  }, [active, jailRef]);
};
